import React, { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { useQuery } from '@tanstack/react-query';
import { Player } from './Player';
import type { PlayerProps } from './types';
import { fetchVideoData, type VideoData } from './api';
import { Loading, ErrorScreen, Title } from './components';

// Import helper functions
import { findHLSPlaylist, getErrorType } from './helper';
import useMessages, { type Translations } from './messages/useMessages';

export interface VideoProps extends Omit<PlayerProps, 'src'> {
  videoId?: string;
  publicKey?: string;
  videoData?: VideoData;
  refetchInterval?: number;
  playerName?: string;
  locale?: string;
  events?: {
    onVideoData?: (video: VideoData) => void;
    onEmptyPlaylists?: () => void;
    onError?: (error: Error) => void;
    onPlay?: () => void;
    onPause?: () => void;
    onEnded?: () => void;
    onLoadStart?: () => void;
    onCanPlay?: () => void;
    /** Fires once the Shaka player instance is created (before manifest load).
     * The instance exposes `player.mux.updateData(...)` for mid-view metadata
     * updates (e.g. attaching consent-gated ids after a CMP grant). */
    onPlayerReady?: (player: any) => void;
    onAdStart?: () => void;
    onAdComplete?: () => void;
    onAdError?: (error: any) => void;
    onAdSkipped?: () => void;
    onAdPaused?: () => void;
    onAdResumed?: () => void;
    onAdProgress?: (adProgressData: any) => void;
    onAllAdsCompleted?: () => void;
  };
  children?: React.ReactNode;
  className?: string;
  auth?: {
    mottoToken?: string;
    userId?: string;
  };
  settings?: {
    backgroundImageUrl?: string;
  };
  // TanStack Query options
  queryOptions?: {
    enabled?: boolean;
    staleTime?: number;
    cacheTime?: number;
    retry?: number;
    retryDelay?: number;
  };
  adsEnabled?: boolean;
}

export const Video: React.FC<VideoProps> = ({
  videoId,
  publicKey,
  videoData: providedVideoData,
  refetchInterval = 0,
  playerName,
  locale = 'en',
  events,
  children,
  className,
  auth,
  settings,
  queryOptions = {},
  adsEnabled = false,
  ...props
}) => {
  // Use TanStack Query for data fetching
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<VideoData>({
    queryKey: ['video', videoId, publicKey, auth?.mottoToken, adsEnabled, locale],
    queryFn: () => fetchVideoData(videoId!, publicKey!, auth?.mottoToken, adsEnabled, locale),
    enabled: !!videoId && !!publicKey && !providedVideoData,
    refetchInterval: refetchInterval > 0 ? refetchInterval : false,
    staleTime: queryOptions.staleTime ?? 5 * 60 * 1000, // 5 minutes
    gcTime: queryOptions.cacheTime ?? 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)
    retry: queryOptions.retry ?? 3,
    retryDelay: queryOptions.retryDelay ?? ((attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000))
  });

  const video = providedVideoData || data;
  const { t } = useMessages(locale);
  
  // Find HLS playlist using helper function
  const activePlaylist = findHLSPlaylist(video);
  const activePlaylistUrl = (
      activePlaylist?.url ??
      activePlaylist?.drm?.widevine?.playlistUrl ??
      activePlaylist?.drm?.playready?.playlistUrl ??
      activePlaylist?.drm?.fairplay?.playlistUrl);
  const activePlaylistHasUrl = !!activePlaylistUrl;

  // Notify parent of video data
  useEffect(() => {
    if (events?.onVideoData && video) {
      events.onVideoData(video);
    }
  }, [video, events]);

  // Loading state
  if (isLoading || (!providedVideoData && !video)) {
    return (
      <div className={twMerge("md:rounded-2xl overflow-hidden aspect-video", className)}>
        <div className="relative w-full h-full bg-[#151515]">
          <Loading />
        </div>
      </div>
    );
  }

  // Handle empty playlists
  if (!isLoading && video && !activePlaylistHasUrl && events?.onEmptyPlaylists) {
    events.onEmptyPlaylists();
  }

  // Error state
  if (error || video?.error) {
    const errorKey = video?.error ? getErrorType(undefined, video) : 'API_ERROR';
    const errorObj = error || new Error(video?.error || 'Unknown error');
    
    if (events?.onError) {
      events.onError(errorObj);
    }

    // Use localized error messages
    const title = t(errorKey as keyof Translations) || t('DEFAULT_ERROR');
    const description = t(`${errorKey}_DESCRIPTION` as keyof Translations) || t('DEFAULT_ERROR_DESCRIPTION');

    return (
      <div className={twMerge("md:rounded-2xl overflow-hidden aspect-video", className)}>
        <div className="relative w-full h-full">
          <ErrorScreen
            title={title}
            description={description}
          />
          {children}
        </div>
      </div>
    );
  }

  // No playable source or missing playlist
  if (!activePlaylist || !activePlaylistHasUrl) {
    return (
      <div className={twMerge("md:rounded-2xl overflow-hidden aspect-video", className)}>
        <div className="relative w-full h-full bg-[#151515]">
          <Title title={video?.name || ""} />
          {children}
        </div>
      </div>
    );
  }

  // Render video player with source
  return (
    <div className={twMerge("md:rounded-2xl overflow-hidden aspect-video", className)}>
      <div className="relative w-full h-full">
        <Player
          {...props}
          src={activePlaylist}
          managedMode={true}
          className={twMerge("video-player-container", className)}
          events={events}
          locale={locale}
          containerClassName="w-full h-full"
          publicKey={publicKey}
          auth={auth}
          {...(adsEnabled && video?.ad?.adTagUrl ? { imaConfig: { adTagUrl: video?.ad?.adTagUrl } } : {})}
        >
          {children}
        </Player>
      </div>
    </div>
  );
}; 